import { HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { computed, inject, Injectable, Signal } from '@angular/core';

/**
 * libs
 */
import { TranslateService } from '@ngx-translate/core';

/**
 * interfaces
 */
import { IResponse } from '@network';
import { ILookupItem } from './interfaces';

/**
 * constants
 */
import { UserConst } from '@identity/user';
import { LOOKUP_FA_ICONS } from './consts/lookup-icons.const';

/**
 * models
 */
import { LookupItemModel } from './models';
import {
  FacilityConst, UnitTypesConst,
  UnitsConst, IUnitTypeDto, IUnitDto
} from '@featureModule/units-management/modules';
import { DeviceRegistryConst } from '@featureModule/system-settings/modules/device/modules/device-registry/consts';

/**
 * Service for managing lookup data used in dropdowns and select components.
 * 
 * This service provides cached lookup data (like roles, statuses, etc.) that can be
 * used throughout the application. It uses RxJS shareReplay(1) for caching and
 * converts Observables to Signals for safe template usage, preventing infinite loops.
 * 
 * @example
 * ```typescript
 * constructor(private lookupService: LookupService) {}
 * 
 * // Use the signal directly in template
 * roles = this.lookupService.rolesLookup;
 * ```
 */
@Injectable()
export class LookupService {

  /**
   * The Angular HTTP client used to make requests.
   */
  private readonly _http: HttpClient = inject(HttpClient);

  /**
   * Translate service
   * @type {TranslateService}
   * @description The translate service
   */
  private readonly _translateService: TranslateService = inject(TranslateService);

  /**
   * Signal for the current language.
   */
  private readonly _langSignal: Signal<string> = toSignal(
    this._translateService.onLangChange.pipe(map((event) => event.lang)),
    { initialValue: this._translateService.getCurrentLang() }
  );

  /**
   * Signal for statuses lookup data.
   * @returns Signal containing array of lookup items with id, name, and value
   */
  public readonly statusesLookup: Signal<ILookupItem[]> = computed(() => {
    const _statuses = [
      { id: '1', name: this._translateService.instant('STATUS.ACTIVE'), value: true, translateKey: 'STATUS.ACTIVE' },
      { id: '0', name: this._translateService.instant('STATUS.INACTIVE'), value: false, translateKey: 'STATUS.INACTIVE' },
    ]
    return this.detectByChangeLanguage(_statuses);
  })

  /**
   * Signal for roles lookup data.
   *
   * This signal provides reactive access to roles data and is safe to use
   * in templates without causing infinite loops. The data is cached after
   * the first HTTP request.
   *
   * @returns Signal containing array of lookup items with id and name
   */
  public readonly rolesLookup$: Observable<ILookupItem[]> = this._GET_ROLES_LOOKUP()
        .pipe(shareReplay({ bufferSize: 1, refCount: false }))

  /**
   * Signal for facility types lookup data.
   *
   * Fetches facility types from the API once and caches the result.
   * Safe to use in templates and across multiple components system-wide.
   *
   * @returns Signal containing array of lookup items with id and name
   */
  public readonly facilityTypesLookup$: Observable<ILookupItem<undefined>[]> = this._GET_FACILITY_TYPES_LOOKUP()
        .pipe(shareReplay({ bufferSize: 1, refCount: false }))

  /**
   * Cached observable for unit types lookup data.
   *
   * Fetches all unit types from the API once and caches the result.
   * Each item carries `value = default_level` (number) so the form
   * can filter the list client-side by the target hierarchy level.
   *
   * @returns Observable emitting an array of lookup items with id, name, and default_level as value.
   */
  public readonly unitTypesLookup$: Observable<ILookupItem<number>[]> = this._GET_UNIT_TYPES_LOOKUP()
        .pipe(shareReplay({ bufferSize: 1, refCount: false }))

  /**
   * Cached observable for first-level units lookup data.
   *
   * Fetches the direct children of the root unit once and caches the result.
   * Intended for use by other modules that need a flat list of top-level units
   * (e.g. assignment selects, filters).
   *
   * @returns Observable emitting an array of lookup items with id and name.
   */
  public readonly unitsFirstLevelLookup$: Observable<ILookupItem[]> = this._GET_UNITS_FIRST_LEVEL_LOOKUP()
        .pipe(shareReplay({ bufferSize: 1, refCount: false }))

  /**
   * Cached observable for the buildings list.
   *
   * Fetches all building nodes from the units hierarchy once and caches the result.
   * Each item carries `id` and `name` only — used to populate the Building dropdown
   * in the Device Registry form, which in turn filters the Unit dropdown.
   *
   * @returns Observable emitting an array of lookup items with id and name.
   */
  public readonly buildingList$: Observable<ILookupItem[]> = this._GET_BUILDING_LIST_LOOKUP()
        .pipe(shareReplay({ bufferSize: 1, refCount: false }))

  /**
   * Cached observable for the SADIN clients list.
   *
   * Fetches all registered SADIN clients once and caches the result.
   * Each item carries `value = code` (the short client code) so both
   * `client_id` (id) and `client_code` (value) are available from one selection.
   *
   * @returns Observable emitting an array of lookup items with id, name, and code as value.
   */
  public readonly sadinClientList$: Observable<ILookupItem<string>[]> = this._GET_SADIN_CLIENTS_LOOKUP()
        .pipe(shareReplay({ bufferSize: 1, refCount: false }))

  /**
   * Cached observable for font-awesome icons lookup data.
   * Currently served from a static list; replace `of(LOOKUP_FA_ICONS)` with
   * an HTTP call when the server endpoint is available.
   */
  public readonly iconsLookup$: Observable<LookupItemModel<string>[]> = of(LOOKUP_FA_ICONS)
        .pipe(shareReplay({ bufferSize: 1, refCount: false }))

  /**
   * 
   */
  constructor() {}

  /**
   * Detect by change language
   * @param data The data to detect by change language.
   * @returns The data detected by change language.
   * @description The data is detected by change language and the language is detected by the langSignal.
   */
  public detectByChangeLanguage<TData = any[]>(data: TData): TData {
    const _lang = this._langSignal();
    return data;
  }

  /**
   * Private cached Observable for facility types lookup.
   * Uses shareReplay(1) to cache the HTTP response and prevent multiple API calls.
   * @returns An observable that emits the facility types lookup data.
   */
  private _GET_FACILITY_TYPES_LOOKUP(): Observable<ILookupItem[]> {
    return this._lookupHandler<ILookupItem[]>(FacilityConst.GET_FACILITY_TYPES_ENDPOINT)
                .pipe(
                  map((types) => types.map((type) => new LookupItemModel(type.id, type.name))),
                );
  }

  /**
   * Private cached Observable for roles lookup.
   * Uses shareReplay(1) to cache the HTTP response and prevent multiple API calls.
   * @returns An observable that emits the roles lookup data.
   */
  private _GET_ROLES_LOOKUP<TRoleDto extends { id: string; name: string }>(): Observable<ILookupItem[]> {
    return this._lookupHandler<{roles: TRoleDto[]}>(UserConst.GET_LIST_OF_ROLES_ENDPOINT)
                .pipe(
                  map((response) => response.roles),
                  map((roles) => roles.map((role) => new LookupItemModel(role.id, role.name))),
                );
  }

  /**
   * Fetches all unit types and maps them to lookup items.
   * The `value` field carries `default_level` for client-side level filtering.
   */
  private _GET_UNIT_TYPES_LOOKUP(): Observable<ILookupItem<number>[]> {
    return this._lookupHandler<IUnitTypeDto[]>(UnitTypesConst.LIST_ENDPOINT)
                .pipe(
                  map((types) => types.map((type) => new LookupItemModel<number>(type.id, type.name, type.default_level))),
                );
  }

  /**
   * Fetches the first-level units and maps them to simple id/name lookup items.
   */
  private _GET_UNITS_FIRST_LEVEL_LOOKUP(): Observable<ILookupItem[]> {
    return this._lookupHandler<IUnitDto[]>(UnitsConst.FIRST_LEVEL_ENDPOINT)
                .pipe(
                  map((units) => units.map((unit) => new LookupItemModel(unit.id, unit.name))),
                );
  }

  /**
   * Fetches all building nodes and maps them to simple id/name lookup items.
   */
  private _GET_BUILDING_LIST_LOOKUP(): Observable<ILookupItem[]> {
    return this._lookupHandler<{ id: string; name: string }[]>(DeviceRegistryConst.BUILDING_LIST_ENDPOINT)
                .pipe(
                  map((buildings) => buildings.map((b) => new LookupItemModel(b.id, b.name))),
                );
  }

  /**
   * Fetches all SADIN clients and maps them to lookup items.
   * The `value` field carries the short client `code` so the form can populate
   * both `client_id` (id) and `client_code` (value) from a single selection.
   */
  private _GET_SADIN_CLIENTS_LOOKUP(): Observable<ILookupItem<string>[]> {
    return this._lookupHandler<{ id: string; name: string; code: string }[]>(DeviceRegistryConst.SADIN_CLIENTS_ENDPOINT)
                .pipe(
                  map((clients) => clients.map((c) => new LookupItemModel<string>(c.id, c.name, c.code))),
                );
  }

  /**
   * Private helper method to handle HTTP requests and convert responses to the expected type.
   * @param endpoint The API endpoint to fetch the data from.
   * @returns An observable that emits the data from the API response.
   */
  private _lookupHandler<T>(endpoint: string): Observable<T> {
    return this._http
                .get<IResponse<T>>(endpoint)
                .pipe(
                  map((response) => response.data),
                  catchError(() => of([] as T))
                );
  }  


}

